import React from "react";
import { Button, message, Modal, Tag, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { InstitutionCell } from "./InstitutionCell";
import type {
  Account,
  Connection,
} from "@/providers/smarta-banking/banking-api-client";
import { ApiPaths } from "@/providers/smarta-banking/banking-api-client";
import { Label, Tbody, Td, Tr, Value } from "../../../components/styles";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { AccountRow } from "./AccountRow";
import { api } from "../../client";
import { usePollingCallback } from "@/lib/hooks/usePollingCallback";

const getTexts = (t: TFunction) => ({
  labels: {
    archivedAt: t("connectionTable.labels.archivedAt"),
    accessValidUntil: t("connectionTable.labels.accessValidUntil"),
    expiredAt: t("connectionTable.labels.expiredAt"),
    agreementAcceptedAt: t("connectionTable.labels.agreementAcceptedAt"),
    historicalStartDate: t("connectionTable.labels.historicalStartDate"),
    archived: t("connectionTable.labels.archived"),
    expired: t("connectionTable.labels.expired"),
    active: t("connectionTable.labels.active"),
  },
  archiveModal: {
    title: t("connectionTable.archiveModal.title"),
    content: t("connectionTable.archiveModal.content"),
    okText: t("connectionTable.archiveModal.okText"),
    cancelText: t("connectionTable.archiveModal.cancelText"),
  },
  archiveButton: {
    archive: t("connectionTable.archiveButton.archive"),
    archiving: t("connectionTable.archiveButton.archiving"),
  },
  messages: {
    archivalSuccess: t("connectionTable.messages.archivalSuccess"),
    archivalError: t("connectionTable.messages.archivalError"),
  },
});

export type ConnectionTableData = Connection & {
  type: "connection";
  accounts: Account[];
};

type Props = {
  data: ConnectionTableData;
  customerId: string;
};

export const ConnectionTable: React.FC<Props> = ({ data, customerId }) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const queryClient = useQueryClient();

  const { isPolling, startPolling } = usePollingCallback({
    interval: 2000,
    maxAttempts: 15, // 30 seconds max
  });

  const {
    id: connectionId,
    institutionId,
    agreementAcceptedAt,
    historicalStartDate,
    accessValidUntil,
    archivedAt,
    expiredAt,
    accounts,
  } = data;

  const { mutate: archiveConnection, isPending: isArchiving } = api.useMutation(
    "post",
    ApiPaths.postApiCustomersByCustomerIdConnectionsByConnectionIdArchive,
    {
      onSuccess: async () => {
        message.success(texts.messages.archivalSuccess);

        startPolling(async () => {
          // Refetch and wait for the result
          const [connectionsResult] = await Promise.all([
            queryClient.fetchQuery({
              queryKey: [
                "get",
                ApiPaths.getApiCustomersByCustomerIdConnections,
                { params: { path: { customerId } } },
              ],
            }),
            queryClient.invalidateQueries({
              queryKey: ["get", ApiPaths.getApiCustomersByCustomerIdAccounts],
            }),
          ]);

          const queryData = connectionsResult as
            | { data: Connection[] }
            | undefined;
          const connection = queryData?.data?.find(
            (c) => c.id === connectionId
          );
          return !!connection?.archivedAt;
        });
      },
      onError: (error) => {
        message.error(error.error?.message || texts.messages.archivalError);
      },
    }
  );

  const handleArchiveClick = () => {
    Modal.confirm({
      title: texts.archiveModal.title,
      icon: <ExclamationCircleOutlined />,
      content: texts.archiveModal.content,
      okText: texts.archiveModal.okText,
      cancelText: texts.archiveModal.cancelText,
      onOk: () => {
        archiveConnection({
          params: {
            path: {
              customerId,
              connectionId,
            },
          },
        });
      },
    });
  };

  return (
    <Tbody>
      <Tr isHeaderRow hasBorder>
        <InstitutionCell institutionId={institutionId} />
        <Td>
          <Label>{texts.labels.historicalStartDate}</Label>
          <Value>
            {moment(historicalStartDate).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
        </Td>
        <Td>
          <Label>{texts.labels.agreementAcceptedAt}</Label>
          <Value>
            {moment(agreementAcceptedAt).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
        </Td>
        <Td>
          <Label>{texts.labels.accessValidUntil}</Label>
          <Value>
            {moment(accessValidUntil).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
        </Td>
        <Td>
          {(archivedAt ?? expiredAt) && (
            <>
              <Label>
                {archivedAt ? texts.labels.archivedAt : texts.labels.expiredAt}
              </Label>
              <Value>
                {moment(archivedAt ?? expiredAt).format(
                  DISPLAY_DATE_TIME_FORMAT
                )}
              </Value>
            </>
          )}
        </Td>
        <Td className="text-center">
          {archivedAt ? (
            <Tag>
              <Typography.Text type="secondary">
                {texts.labels.archived}
              </Typography.Text>
            </Tag>
          ) : expiredAt ? (
            <Tag color="warning">{texts.labels.expired}</Tag>
          ) : (
            <Tag color="success">{texts.labels.active}</Tag>
          )}
        </Td>
        <Td className="text-right">
          <Button
            type="link"
            size="small"
            onClick={handleArchiveClick}
            loading={isArchiving || isPolling}
            disabled={!!archivedAt || !!expiredAt || isArchiving || isPolling}
          >
            {isArchiving || isPolling
              ? texts.archiveButton.archiving
              : texts.archiveButton.archive}
          </Button>
        </Td>
      </Tr>
      {accounts.map((account) => (
        <AccountRow
          key={account.id}
          customerId={customerId}
          account={account}
        />
      ))}
    </Tbody>
  );
};
